import React, { useState } from 'react';
import { AlertTriangle, X, Trash2, ArrowLeft } from 'lucide-react';
import './DeleteAccountModal.css';

/**
 * DeleteAccountModal - Two-step confirmation modal for account deletion
 * 
 * Step 1: Warning and initial confirmation
 * Step 2: Final confirmation with checkbox + text input ("DELETE")
 */
export const DeleteAccountModal = ({ isOpen, onClose, onConfirmDelete, userEmail }) => {
  const [step, setStep] = useState(1);
  const [understood, setUnderstood] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setStep(1);
      setUnderstood(false);
      setConfirmationText('');
      setError('');
      setIsDeleting(false);
    }
  }, [isOpen]);

  const handleContinue = () => {
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
    setUnderstood(false);
    setConfirmationText('');
    setError('');
  };

  const handleDelete = async () => {
    if (!understood || confirmationText.toUpperCase() !== 'DELETE') {
      setError('Please complete all confirmations');
      return;
    }

    setIsDeleting(true);
    setError('');

    try {
      await onConfirmDelete({
        understood: true,
        confirmation_text: confirmationText
      });
    } catch (err) {
      setError(err.message || 'Failed to delete account');
      setIsDeleting(false);
    }
  };

  const isDeleteEnabled = understood && confirmationText.toUpperCase() === 'DELETE';

  if (!isOpen) return null;

  return (
    <div className="delete-account-modal-overlay" onClick={onClose}>
      <div className="delete-account-modal" onClick={e => e.stopPropagation()}>
        <button className="delete-modal-close" onClick={onClose}>
          <X size={20} />
        </button>

        {step === 1 && (
          <div className="delete-modal-content">
            <div className="delete-modal-icon warning">
              <AlertTriangle size={48} />
            </div>
            <h2>Delete your account?</h2>
            <p className="delete-modal-description">
              This will permanently delete your Golf Nuts Clubhouse account and remove your access to:
            </p>
            <ul className="delete-consequences">
              <li>Your profile and all your data</li>
              <li>Giveaway entries and contest eligibility</li>
              <li>Member directory listing</li>
              <li>Handicap tracking history</li>
            </ul>
            <p className="delete-note">
              <strong>Note:</strong> You&apos;ll receive an email with instructions to recover your account if you change your mind.
            </p>
            
            <div className="delete-modal-actions">
              <button className="btn-cancel" onClick={onClose}>
                Cancel
              </button>
              <button className="btn-continue" onClick={handleContinue}>
                Continue
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="delete-modal-content">
            <div className="delete-modal-icon danger">
              <Trash2 size={48} />
            </div>
            <h2>Final confirmation</h2>
            <p className="delete-modal-description">
              This action is permanent unless you recover your account later.
            </p>

            {error && (
              <div className="delete-error-message">
                {error}
              </div>
            )}

            <div className="delete-confirmation-form">
              <label className="delete-checkbox-label">
                <input
                  type="checkbox"
                  checked={understood}
                  onChange={(e) => setUnderstood(e.target.checked)}
                />
                <span>I understand this will delete my account and remove my access.</span>
              </label>

              <div className="delete-text-confirm">
                <label>Type <strong>DELETE</strong> to confirm:</label>
                <input
                  type="text"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder="DELETE"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="delete-modal-actions">
              <button className="btn-back" onClick={handleBack}>
                <ArrowLeft size={16} /> Back
              </button>
              <button 
                className="btn-delete-final"
                onClick={handleDelete}
                disabled={!isDeleteEnabled || isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className="spinner"></span>
                    Deleting...
                  </>
                ) : (
                  'Permanently Delete My Account'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeleteAccountModal;
